import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [guestData, setGuestData] = useState({ email: '', mobile: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const { login, guestStart } = useAuth();
  const { t, dir } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(formData, 'user');
    if (result.success) navigate(redirectTo);
    setLoading(false);
  };

  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    setGuestLoading(true);
    const result = await guestStart(guestData);
    if (result.success) navigate(redirectTo);
    setGuestLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px]">
        <div className="auth-card px-7 py-9 sm:px-10">
          <Link to="/" className="auth-back">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('backHome')}
          </Link>
          <h1 className="text-[1.75rem] font-medium text-gray-900 tracking-tight">{t('signIn')}</h1>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">
            {t('signInLead')}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="login-email" className={`block text-sm font-medium text-gray-700 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                {t('email')}
              </label>
              <div className="relative">
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  dir={dir}
                  placeholder={t('phEmail')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="auth-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                  {t('password')}
                </label>
                <Link to="/forgot-password" className="nav-link text-sm text-purple-700 hover:text-purple-900">
                  {t('forgot')}
                </Link>
              </div>
              <div className="relative">
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  dir={dir}
                  placeholder={t('phEnterPassword')}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="auth-input pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? t('signingIn') : t('signIn')}
            </button>
          </form>

          {/* Quick (guest) checkout */}
          <div className="mt-7 pt-6 border-t border-[#f3f2f6]">
            <div className="flex items-center gap-3 mb-4">
              <span className="flex-1 border-t border-[#f3f2f6]" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{t('orDivider')}</span>
              <span className="flex-1 border-t border-[#f3f2f6]" />
            </div>
            <h2 className={`text-base font-semibold text-gray-900 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('quickCheckout')}</h2>
            <p className={`mt-1 text-sm text-gray-500 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('quickCheckoutLead')}</p>

            <form onSubmit={handleGuestSubmit} className="mt-4 space-y-4">
              <input
                type="email"
                required
                autoComplete="email"
                dir={dir}
                placeholder={t('phEmail')}
                value={guestData.email}
                onChange={(e) => setGuestData({ ...guestData, email: e.target.value })}
                className="auth-input auth-input-plain"
              />
              <input
                type="tel"
                required
                autoComplete="tel"
                dir={dir}
                placeholder={t('mobileNumber')}
                value={guestData.mobile}
                onChange={(e) => setGuestData({ ...guestData, mobile: e.target.value })}
                maxLength="20"
                className="auth-input auth-input-plain"
              />
              <button
                type="submit"
                disabled={guestLoading}
                className="w-full h-12 rounded-[14px] border-2 border-purple-700 text-purple-700 font-medium text-[0.95rem] hover:bg-purple-50 transition disabled:opacity-60"
              >
                {guestLoading ? t('pleaseWait') : t('continueAsGuest')}
              </button>
            </form>
          </div>

          <p className="mt-7 pt-6 text-center text-sm text-gray-500 border-t border-[#f3f2f6]">
            {t('newHere')}{' '}
            <Link to="/register" className="nav-link font-medium text-purple-700 hover:text-purple-900">
              {t('createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
